import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { cn } from '../lib/cn.js';
/** Render the label for each artifact kind. Verbatim designer copy. */
const DEFAULT_KIND_LABELS = {
    brief: 'This brief',
    dossier: 'This dossier',
    'ask-trace': 'This answer',
};
/** Default sentiment labels — verbatim designer copy with leading glyphs. */
const DEFAULT_SENTIMENT_LABELS = {
    useful: '↑ useful',
    meh: '— meh',
    noise: '↓ noise',
};
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const SUPPRESS_THRESHOLD = 3;
function storageKey(kind) {
    return `fb-microbar-suppress:${kind}`;
}
/**
 * Read + parse the suppression record. Defensive: any malformed JSON
 * resets to a fresh state rather than throwing. Storage access is also
 * try/catch'd because Safari private mode + storage-blocked environments
 * throw on `getItem` even though `window.localStorage` is truthy — same
 * pattern as `apps/web/src/lib/active-org.ts` + `walkthrough.tsx`.
 */
function readSuppress(kind) {
    if (typeof window === 'undefined' || !window.localStorage) {
        return { ignoreCount: 0 };
    }
    let raw;
    try {
        raw = window.localStorage.getItem(storageKey(kind));
    }
    catch {
        return { ignoreCount: 0 };
    }
    if (!raw)
        return { ignoreCount: 0 };
    try {
        const parsed = JSON.parse(raw);
        if (typeof parsed === 'object' &&
            parsed !== null &&
            typeof parsed.ignoreCount === 'number') {
            return parsed;
        }
    }
    catch {
        // fall through to fresh state
    }
    return { ignoreCount: 0 };
}
function writeSuppress(kind, state) {
    if (typeof window === 'undefined' || !window.localStorage)
        return;
    try {
        window.localStorage.setItem(storageKey(kind), JSON.stringify(state));
    }
    catch {
        // Quota / disabled / private — suppression is best-effort.
    }
}
/** True if the kind is currently within its 7-day suppression window. */
function isSuppressed(state, now) {
    if (!state.suppressedUntil)
        return false;
    return new Date(state.suppressedUntil).getTime() > now.getTime();
}
export function EndOfReadMicrobar({ artifactKind, onVote, onDismiss, kindLabel, sentimentLabels, dismissLabel = 'Dismiss', now, className, }) {
    const labels = useMemo(() => ({ ...DEFAULT_SENTIMENT_LABELS, ...sentimentLabels }), [sentimentLabels]);
    const effectiveKindLabel = kindLabel ?? DEFAULT_KIND_LABELS[artifactKind];
    const [visible, setVisible] = useState(() => {
        const state = readSuppress(artifactKind);
        return !isSuppressed(state, now ?? new Date());
    });
    // If artifactKind changes (parent reuses the same component instance
    // for a different surface), re-read suppression for the new kind.
    useEffect(() => {
        const state = readSuppress(artifactKind);
        setVisible(!isSuppressed(state, now ?? new Date()));
    }, [artifactKind, now]);
    function handleVote(s) {
        // A vote clears the ignore streak — the user engaged.
        writeSuppress(artifactKind, { ignoreCount: 0 });
        setVisible(false);
        onVote(s);
    }
    function handleDismiss() {
        const state = readSuppress(artifactKind);
        const nextCount = state.ignoreCount + 1;
        const next = { ignoreCount: nextCount };
        if (nextCount >= SUPPRESS_THRESHOLD) {
            next.suppressedUntil = new Date((now ?? new Date()).getTime() + ONE_WEEK_MS).toISOString();
        }
        writeSuppress(artifactKind, next);
        setVisible(false);
        onDismiss?.();
    }
    if (!visible)
        return null;
    return (_jsxs("div", { role: "group", "aria-label": "Was this useful?", className: cn('inline-flex items-center gap-2 rounded-pill border border-[var(--color-line)]', 'bg-[var(--color-surface-2)] px-2.5 py-1 text-xs text-[var(--color-ink-secondary)]', className), children: [_jsx("span", { className: "font-mono text-2xs uppercase tracking-[0.14em] text-[var(--color-ink-muted)]", children: effectiveKindLabel }), _jsx("span", { "aria-hidden": true, className: "text-[var(--color-ink-faint)]", children: "\u00B7" }), _jsx(MicroChip, { onClick: () => handleVote('useful'), tone: "sage", children: labels.useful }), _jsx(MicroChip, { onClick: () => handleVote('meh'), tone: "neutral", children: labels.meh }), _jsx(MicroChip, { onClick: () => handleVote('noise'), tone: "rose", children: labels.noise }), _jsx("button", { type: "button", "aria-label": dismissLabel, onClick: handleDismiss, className: "j-press inline-flex size-[18px] items-center justify-center rounded-1 text-[var(--color-ink-faint)] transition-colors hover:bg-[var(--color-surface-3)] hover:text-[var(--color-ink-muted)]", children: "\u00D7" })] }));
}
function MicroChip({ children, onClick, tone, }) {
    return (_jsx("button", { type: "button", onClick: onClick, className: cn('j-press inline-flex items-center rounded-pill px-2 py-0.5 text-xs transition-colors', tone === 'sage' && 'text-[var(--color-sage)] hover:bg-[var(--color-sage-soft)]', tone === 'rose' && 'text-[var(--color-rose)] hover:bg-[var(--color-rose-soft)]', tone === 'neutral' && 'text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-3)]'), children: children }));
}
